import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { 
  RefreshCw, 
  Sparkles, 
  AlertCircle,
  Loader2,
  FileText,
  Calendar,
  User
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { getDefaultProvider } from '@/ai/providers';
import { getRecommendedModel } from '@/ai/models';
import { generateAIText } from "@/lib/ai-text";

interface ExecutiveSummary {
  id: string;
  policy_id: string;
  company_id: string;
  created_by: string;
  summary_text: string;
  created_at: string;
  updated_at: string;
}

interface ExecutiveSummaryProps {
  policyId: string;
  policyTitle: string;
  policyContent: string;
  policyCreatedBy: string;
  isPolicyCreator: boolean;
}

const ExecutiveSummary = ({ 
  policyId, 
  policyTitle, 
  policyContent, 
  policyCreatedBy, 
  isPolicyCreator 
}: ExecutiveSummaryProps) => {
  const { profile } = useAuth();
  const { toast } = useToast();
  const [summary, setSummary] = useState<ExecutiveSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // Load existing executive summary
  const loadSummary = async () => {
    if (!profile?.company_id) return;

    try {
      const { data: summaryData, error: summaryError } = await supabase
        .from('executive_summaries')
        .select('*')
        .eq('policy_id', policyId)
        .eq('company_id', profile.company_id)
        .single();

      if (summaryError && summaryError.code !== 'PGRST116') {
        throw summaryError;
      }

      if (summaryData) {
        setSummary(summaryData);
      }
    } catch (error: any) {
      console.error('Error loading executive summary:', error);
    } finally {
      setLoading(false);
    }
  };

  // Generate AI executive summary
  const generateSummary = async () => {
    if (!profile?.company_id) return;

    setGenerating(true);
    try {
      // Get default provider (user preference or fallback)
      const provider = getDefaultProvider();
      const model = getRecommendedModel(provider, 'ANALYSIS');

      // Create AI prompt for executive summary generation
      const prompt = `You are a corporate policy expert. Based on the following policy, generate a comprehensive executive summary that provides a clear, high-level overview of the policy for senior management and stakeholders.

Policy Title: ${policyTitle}

Policy Content:
${policyContent}

The executive summary should:
1. Be 2-3 paragraphs long (approximately 300-500 words)
2. Highlight the key objectives and scope of the policy
3. Summarize the main requirements and responsibilities
4. Emphasize the business impact and compliance benefits
5. Be written in professional, executive-level language
6. Focus on strategic implications rather than operational details

Format your response as plain text without any markdown formatting or special characters.`;

      // Use client-side AI to generate summary
      const summaryText = await generateAIText({
        provider,
        model,
        messages: [
          {
            role: 'user',
            content: prompt
          }
        ],
        temperature: 0.3,
      });
      
      // Validate that we have a meaningful summary
      if (!summaryText || summaryText.trim().length < 100) {
        throw new Error('Generated summary is too short. Please try again.');
      }

      // Create or update executive summary in database
      const { data: summaryData, error: summaryError } = await supabase
        .from('executive_summaries')
        .upsert({
          policy_id: policyId,
          company_id: profile.company_id,
          created_by: profile.id,
          summary_text: summaryText.trim(),
        })
        .select()
        .single();

      if (summaryError) throw summaryError;

      // Reload summary
      await loadSummary();

      toast({
        title: "Success",
        description: "Executive summary generated successfully!",
      });

    } catch (error: any) {
      console.error('Error generating executive summary:', error);
      toast({
        title: "Error",
        description: "Failed to generate executive summary: " + (error.message || "Unknown error"),
        variant: "destructive",
      });
    } finally {
      setGenerating(false);
      setIsDialogOpen(false);
    }
  };

  useEffect(() => {
    loadSummary();
  }, [policyId, profile?.company_id]);

  if (loading) {
    return (
      <Card className="mt-6">
        <CardContent className="p-6">
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin mr-2" />
            <span className="text-muted-foreground">Loading executive summary...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mt-6">
      <CardHeader>
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <FileText className="w-5 h-5 text-blue-600 flex-shrink-0" />
            <CardTitle className="text-lg truncate">Executive Summary</CardTitle>
            <Badge variant="secondary" className="ml-2 hidden sm:inline-flex">
              AI Generated
            </Badge>
          </div>
          {isPolicyCreator && (
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" disabled={generating} className="flex-shrink-0">
                  <RefreshCw className={`w-4 h-4 mr-1 sm:mr-2 ${generating ? 'animate-spin' : ''}`} />
                  <span className="hidden sm:inline">{generating ? 'Generating...' : 'Regenerate'}</span>
                  <span className="sm:hidden">{generating ? '...' : 'Regen'}</span>
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Regenerate Executive Summary</DialogTitle>
                  <DialogDescription>
                    This will generate a new AI-powered executive summary based on the current policy content. 
                    The existing summary will be replaced.
                  </DialogDescription>
                </DialogHeader>
                <div className="flex justify-end gap-2 pt-4">
                  <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={generateSummary} disabled={generating}>
                    {generating ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" />
                        Generate Summary
                      </>
                    )}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {!summary ? (
          <div className="text-center py-8">
            <div className="flex flex-col items-center gap-4">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                <FileText className="w-8 h-8 text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg font-medium text-foreground mb-2">
                  No Executive Summary Yet
                </h3>
                <p className="text-muted-foreground mb-4 max-w-md">
                  Generate an AI-powered executive summary to provide a clear, high-level overview of this policy for senior management and stakeholders.
                </p>
                {isPolicyCreator && (
                  <Button onClick={generateSummary} disabled={generating}>
                    {generating ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" />
                        Generate Summary
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>Generated on {new Date(summary.created_at).toLocaleDateString()}</span>
              </div>
              {summary.updated_at !== summary.created_at && (
                <div className="flex items-center gap-2">
                  <span>•</span>
                  <span>Updated on {new Date(summary.updated_at).toLocaleDateString()}</span>
                </div>
              )}
            </div>
            
            <div className="prose prose-sm max-w-none">
              <div className="whitespace-pre-wrap text-foreground leading-relaxed">
                {summary.summary_text}
              </div>
            </div>

            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                <div>
                  <h4 className="font-medium text-blue-900 mb-1">
                    Executive Summary Purpose
                  </h4>
                  <p className="text-sm text-blue-800">
                    This AI-generated executive summary provides senior management with a high-level overview of the policy's 
                    key objectives, scope, and business impact. It's designed to help executives quickly understand the policy's 
                    strategic importance and compliance requirements.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ExecutiveSummary;
